// course103.ts

const course103 = {
  id: "course-103",
  title: "Professional Conduct and Ethics",

  modules: [
    {
      id: "module-1",
      title: "INTRODUCTION TO PROFESSIONAL CONDUCT AND ETHICS",
      sections: [
        {
          id: "module-1-section-1",
          title: "MEANING OF PROFESSIONAL CONDUCT",
          heading: "1.1 Meaning of Professional Conduct",
          content: `
Professional conduct refers to the expected standards of behaviour, communication, and responsibility that guide how individuals act in a structured working environment.
It determines how a person performs assigned tasks, interacts with others, and responds to situations in professional or community settings.
In practice, professional conduct is reflected in how consistently an individual:
follows instructions and agreed procedures
communicates respectfully with others
handles responsibilities assigned to them
behaves in both supervised and unsupervised settings
represents the expectations of the working environment
Professional conduct is not limited to formal workplaces. It is equally important in community-based activities where individuals work directly with people in real-life settings.
          `,
        },
        {
          id: "module-1-section-2",
          title: "MEANING OF ETHICS",
          heading: "1.2 Meaning of Ethics",
          content: `
Ethics refers to the moral principles that guide decisions and behaviour, especially in situations where there are no clear instructions or direct supervision.
Ethics help individuals decide what is right, fair, and responsible in complex or sensitive situations.
In practical terms, ethics influence decisions such as:
how to handle private or sensitive information
how to respond to difficult or emotional situations
how to treat different individuals fairly
how to act when personal interest conflicts with duty
how to make decisions that affect others
Ethical behaviour is important because not all situations in field or community work come with clear rules. Individuals often have to apply judgement based on values such as fairness, respect, and responsibility.
          `,
        },
        {
          id: "module-1-section-3",
          title: "IMPORTANCE OF PROFESSIONALISM AND ETHICAL BEHAVIOUR IN VOLUNTEER WORK",
          heading: "1.3 Importance of Professionalism and Ethical Behaviour in Volunteer Work",
          content: `
Professionalism and ethics are essential in volunteer and community work because activities often involve direct engagement with people, families, and sensitive social issues.
When professionalism is applied consistently, it helps to:
build trust between communities and those delivering services
ensure consistency in how programs are implemented
improve communication and cooperation during activities
reduce misunderstandings and conflict in community engagement
Ethical behaviour ensures that:
individuals are treated with dignity and respect
sensitive information is protected
decisions are fair and responsible
vulnerable groups are not exposed to harm or discrimination
In practice, the absence of professionalism or ethics can reduce community confidence, affect participation in programs, and weaken the overall impact of interventions.
          `,
        },
        {
          id: "module-1-section-4",
          title: "HOW PROFESSIONAL CONDUCT REFLECTS ORGANIZATIONAL VALUES",
          heading: "1.4 How Professional Conduct Reflects Organizational Values",
          content: `
Professional conduct is closely linked to how an organization is perceived by the public and the communities it serves.
In practical settings, individuals are often seen as representatives of the organization they are associated with. This means that behaviour in the field becomes part of the organization’s identity.
For example, how someone:
speaks to community members
responds to questions or complaints
behaves during field activities
handles responsibilities and commitments
all contribute to how the organization is viewed.
Consistent professional conduct helps to:
strengthen trust in programs and activities
improve community participation and engagement
support long-term relationships with stakeholders
reinforce the credibility of the work being done
Unprofessional behaviour, even in isolated situations, can affect how entire programs are perceived.

CORE UNDERSTANDING OF MODULE 1
Professional conduct defines behaviour in structured environments, while ethics guides decision-making in situations where judgement is required. Together, they form the foundation of responsible, respectful, and trustworthy practice in community and organizational work.

KEY TAKEAWAY
Professional conduct and ethics are essential for building trust, accountability, and credibility in community and organizational work. When individuals act responsibly and with integrity, they strengthen the impact and reputation of the work they do.
          `,
        },
      ],
    },
    {
      id: "module-2",
      title: "CORE PRINCIPLES OF PROFESSIONAL CONDUCT",
      sections: [
        {
          id: "module-2-section-1",
          title: "INTEGRITY AND HONESTY",
          heading: "2.1 Integrity and Honesty",
          content: `
Integrity refers to consistently acting in accordance with moral and professional standards, even when no one is watching. Honesty involves being truthful, transparent, and accurate in communication and actions.
Integrity is demonstrated when individuals:
provide accurate information
act fairly and consistently
make decisions based on facts rather than personal interests
follow established procedures
protect the interests of beneficiaries and stakeholders
honour commitments and responsibilities
Integrity is particularly important during activities such as community assessments, beneficiary selection, resource distribution, and program implementation.
For example, during a community needs assessment, an individual may discover that some community members are attempting to influence the process to gain access to benefits they do not qualify for. Integrity requires collecting and reporting information accurately, even when there is pressure to alter findings.
Similarly, when distributing support to beneficiaries, integrity requires ensuring that assistance reaches the individuals who were identified through the established selection process. Personal relationships, friendships, or external influence should not affect who receives support.
Honesty is demonstrated through truthful communication and accurate reporting.
          `,
        },
        {
          id: "module-2-section-2",
          title: "ACCOUNTABILITY AND RESPONSIBILITY",
          heading: "2.2 Accountability and Responsibility",
          content: `
Accountability means being answerable for one's actions, decisions, and performance. Responsibility refers to the duty to carry out assigned tasks and obligations effectively.
Professional environments depend on accountability because work often involves collaboration and shared goals.
Individuals demonstrate accountability when they:
complete assigned duties
accept ownership of outcomes
communicate progress and challenges
take corrective action when mistakes occur
follow through on commitments
Responsibility involves understanding what is expected and taking appropriate action without constant supervision.
For example, if an individual is responsible for preparing materials for a community session, accountability means ensuring the materials are ready on time. If challenges arise, they should be communicated early so that solutions can be found before they affect the activity.
Organizations function more effectively when individuals accept responsibility for their work rather than shifting blame to others.
          `,
        },
        {
          id: "module-2-section-3",
          title: "RELIABILITY AND COMMITMENT",
          heading: "2.3 Reliability and Commitment",
          content: `
Reliability refers to consistency in actions and performance. Commitment refers to dedication to responsibilities, objectives, and agreed expectations.
People who are reliable can be depended upon to:
arrive on time
complete tasks as assigned
meet deadlines
follow through on commitments
maintain consistency in performance
Commitment is often demonstrated through actions rather than words. It is reflected in how individuals continue to perform their responsibilities even when tasks become challenging or inconvenient.
For example, a volunteer who consistently attends activities, completes assigned responsibilities, and remains engaged throughout a project demonstrates reliability and commitment.
In contrast, frequent lateness, missed deadlines, repeated absence, or failure to complete assigned responsibilities can affect team performance and reduce confidence in an individual's ability to contribute effectively.
          `,
        },
        {
          id: "module-2-section-4",
          title: "RESPECT FOR OTHERS",
          heading: "2.4 Respect for Others",
          content: `
Respect involves recognizing the dignity, value, and rights of every individual.
Professional environments often bring together people with different experiences, opinions, cultures, ages, educational backgrounds, and social circumstances. Respect allows these differences to exist without creating conflict or exclusion.
Respect is demonstrated through:
polite and considerate communication
active listening
valuing different perspectives
avoiding discriminatory language or behaviour
treating people fairly regardless of status or background
Respect does not require agreement with every opinion. Rather, it requires acknowledging the right of others to express their views and experiences.
For example, during a community discussion, participants may hold beliefs that differ from those facilitating the activity. Respectful engagement involves listening, responding professionally, and maintaining dignity throughout the interaction.
          `,
        },
        {
          id: "module-2-section-5",
          title: "PROFESSIONAL APPEARANCE AND BEHAVIOUR",
          heading: "2.5 Professional Appearance and Behaviour",
          content: `
Professional appearance and behaviour influence how individuals are perceived and how effectively they can perform their roles.
Professional appearance means presenting oneself in a manner that is appropriate for the environment and activity being undertaken. This includes clothing, grooming, and general presentation.
Professional behaviour includes:
punctuality
respectful communication
appropriate body language
adherence to organizational expectations
maintaining composure in difficult situations
Professional appearance is not about expensive clothing or personal style preferences. It is about ensuring that one's appearance does not distract from or negatively affect professional responsibilities.
For example, attending a community engagement activity in attire that is inappropriate for the setting may affect credibility and make it more difficult to build trust with participants.
          `,
        },
        {
          id: "module-2-section-6",
          title: "MAINTAINING A POSITIVE ATTITUDE",
          heading: "2.6 Maintaining a Positive Attitude",
          content: `
A positive attitude refers to approaching responsibilities, challenges, and interactions with openness, willingness, and professionalism.
Maintaining a positive attitude does not mean ignoring difficulties or pretending that problems do not exist. Instead, it involves responding constructively to challenges and focusing on solutions rather than obstacles.
A positive attitude is reflected in:
willingness to learn
adaptability to changing situations
cooperation with others
resilience during difficulties
constructive problem-solving
Community and organizational work often involves unexpected changes. Activities may be delayed, attendance may be lower than expected, resources may be limited, or plans may need adjustment.
Individuals who maintain a positive attitude are better able to adapt to these situations and continue contributing effectively.

CORE UNDERSTANDING OF MODULE 2
Professional conduct is built on integrity, honesty, accountability, responsibility, reliability, commitment, respect, professionalism, and a positive attitude. These principles influence how individuals perform their responsibilities, interact with others, and respond to challenges. Consistent application of these principles strengthens trust, improves teamwork, and supports effective engagement in professional and community settings.

KEY TAKEAWAY
Professional conduct is not defined by a single action but by consistent behaviour over time. Individuals who demonstrate integrity, honesty, accountability, responsibility, reliability, respect, professionalism, and a positive attitude contribute to stronger relationships, more effective teamwork, and greater confidence in the work they perform.
          `,
        },
      ],
    },
    {
      id: "module-3",
      title: "WORKPLACE ETHICS",
      sections: [
        {
          id: "module-3-section-1",
          title: "DOING THE RIGHT THING EVEN WHEN NO ONE IS WATCHING",
          heading: "3.1 Doing the Right Thing Even When No One Is Watching",
          content: `
One of the strongest indicators of ethical behaviour is acting responsibly without the need for supervision.
Many professional environments require individuals to work independently, conduct field activities, collect information, or manage resources without direct oversight. In these situations, ethical behaviour depends on personal values and professional standards rather than fear of punishment.
Doing the right thing means:
following procedures even when shortcuts are available
using resources responsibly
reporting information accurately
respecting confidentiality
completing assigned responsibilities honestly
For example, an individual conducting field data collection may realize that revisiting a household to verify information will take additional time and effort. Ethical conduct requires collecting accurate information rather than estimating responses simply to complete the task more quickly.
Similarly, if transport reimbursement is provided for a specific activity, ethical behaviour requires claiming only legitimate expenses rather than inflating costs for personal gain.
          `,
        },
        {
          id: "module-3-section-2",
          title: "FAIRNESS AND IMPARTIALITY",
          heading: "3.2 Fairness and Impartiality",
          content: `
Fairness involves treating people equitably and making decisions based on objective criteria rather than personal preferences. Impartiality means remaining neutral and avoiding bias when carrying out responsibilities.
Professional and community work often requires decisions that affect individuals, groups, or communities. These decisions must be made fairly to maintain trust and credibility.
Fairness and impartiality require individuals to:
treat all people with equal respect
apply the same standards to everyone
avoid favouritism
make decisions based on evidence and established criteria
remain objective when handling complaints, concerns, or disputes
For example, during a beneficiary selection exercise, eligibility should be determined by agreed criteria rather than personal relationships or external influence.
Similarly, when resolving disagreements within a team, all perspectives should be heard before conclusions are reached.
          `,
        },
        {
          id: "module-3-section-3",
          title: "TRANSPARENCY IN ACTIONS AND DECISIONS",
          heading: "3.3 Transparency in Actions and Decisions",
          content: `
Transparency refers to openness, honesty, and clarity in communication, actions, and decision-making processes.
Transparent practices help build trust because people understand how decisions are made and why particular actions are taken.
Transparency involves:
providing accurate information
communicating clearly about processes and expectations
documenting activities appropriately
avoiding misleading statements
being open about challenges and limitations
For example, if a program has limited resources and cannot support every individual who applies, transparency requires clearly explaining the selection criteria and decision-making process.
Likewise, if an activity experiences delays or challenges, transparent communication helps manage expectations and maintain trust among stakeholders.
          `,
        },
        {
          id: "module-3-section-4",
          title: "RESPECTING ORGANIZATIONAL POLICIES AND PROCEDURES",
          heading: "3.4 Respecting Organizational Policies and Procedures",
          content: `
Policies and procedures are established to guide behaviour, ensure consistency, manage risks, and support accountability.
Respecting policies means understanding and following organizational requirements even when personal preferences differ.
Policies often cover areas such as:
safeguarding and protection
confidentiality and data management
communication protocols
financial accountability
reporting procedures
health and safety requirements
Following policies helps:
protect individuals and communities
reduce errors and misunderstandings
maintain professional standards
ensure consistency across activities
For example, if an organization requires consent before collecting photographs during an activity, ethical conduct requires following this process even when obtaining consent may take additional time.
Similarly, if a reporting procedure exists for concerns or complaints, individuals should use the established process rather than attempting to manage serious issues independently.
          `,
        },
        {
          id: "module-3-section-5",
          title: "AVOIDING CONFLICTS OF INTEREST",
          heading: "3.5 Avoiding Conflicts of Interest",
          content: `
A conflict of interest occurs when personal interests, relationships, or benefits have the potential to influence professional judgement or decision-making.
Conflicts of interest can be financial, personal, social, or professional in nature.
Examples may include:
favouring friends or relatives during beneficiary selection
influencing procurement decisions for personal benefit
using organizational opportunities for private gain
allowing personal relationships to affect professional judgement
Not all conflicts of interest involve wrongdoing. The ethical concern arises when personal interests interfere with fairness, objectivity, or professional responsibilities.
For example, if a close relative applies for a program and an individual is involved in the selection process, the ethical response may be to disclose the relationship and remove themselves from the decision-making process.
Managing conflicts of interest helps protect the integrity of decisions and maintains confidence in organizational processes.
Failure to address conflicts of interest can damage trust, create perceptions of unfairness, and undermine organizational credibility.

CORE UNDERSTANDING OF MODULE 3
Workplace ethics provides the moral foundation for professional behaviour. Ethical conduct requires individuals to act responsibly when unsupervised, treat people fairly, communicate transparently, follow established policies, and avoid situations where personal interests could influence professional decisions. These practices help create trust, accountability, and credibility within organizations and communities.

KEY TAKEAWAY
Ethical behaviour is demonstrated through everyday decisions and actions. Individuals who consistently choose honesty, fairness, transparency, responsibility, and objectivity contribute to stronger organizations, more effective programs, and greater confidence among colleagues, communities, and stakeholders.
          `,
        },
      ],
    },
    {
      id: "module-4",
      title: "RESPECT AND DIVERSITY",
      sections: [
        {
          id: "module-4-section-1",
          title: "TREATING EVERYONE WITH DIGNITY AND RESPECT",
          heading: "4.1 Treating Everyone with Dignity and Respect",
          content: `
Dignity refers to the inherent worth and value of every person. Respect involves recognizing that value and treating individuals in a manner that acknowledges their rights, feelings, perspectives, and experiences.
Treating people with dignity and respect means:
speaking politely and professionally
listening without interruption or judgment
valuing the opinions and experiences of others
avoiding insulting, degrading, or dismissive language
recognizing that every person deserves fair treatment
Respect should not be influenced by factors such as age, social status, educational background, ethnicity, religion, disability, gender, or economic condition.
For example, during a community engagement activity, some participants may have limited formal education while others may be highly educated. Respect requires giving all participants equal opportunity to contribute and ensuring that no one is treated as less important because of their background.
          `,
        },
        {
          id: "module-4-section-2",
          title: "APPRECIATING DIVERSITY AND INCLUSION",
          heading: "4.2 Appreciating Diversity and Inclusion",
          content: `
Diversity refers to the presence of differences among people. These differences may include culture, language, religion, ethnicity, gender, age, disability status, educational background, socioeconomic status, experiences, and perspectives.
Inclusion refers to creating environments where all individuals feel welcomed, valued, respected, and able to participate meaningfully.
Appreciating diversity means recognizing that differences are not obstacles but valuable sources of knowledge, experience, and perspective.
Inclusive practice involves:
ensuring participation opportunities for everyone
considering different needs and circumstances
adapting communication where necessary
avoiding assumptions about individuals or groups
creating environments where people feel accepted
For example, during a community meeting, some participants may be comfortable speaking publicly while others may be hesitant. Inclusive engagement may involve creating multiple opportunities for participation rather than relying solely on open discussion.
          `,
        },
        {
          id: "module-4-section-3",
          title: "PREVENTING DISCRIMINATION AND HARASSMENT",
          heading: "4.3 Preventing Discrimination and Harassment",
          content: `
Discrimination occurs when individuals are treated unfairly or denied opportunities because of personal characteristics such as race, ethnicity, gender, disability, religion, age, nationality, or other protected characteristics.
Harassment refers to unwanted behaviour that creates an intimidating, hostile, humiliating, or offensive environment.
Discrimination and harassment can occur in many forms, including:
verbal comments or jokes
exclusion from activities
unequal treatment
intimidation or threats
offensive gestures or behaviour
unfair decision-making
Professional environments require active efforts to prevent discrimination and harassment.
Individuals contribute to safe and respectful environments by:
treating others fairly
challenging inappropriate behaviour through appropriate channels
reporting concerns when necessary
promoting respectful interactions
avoiding stereotypes and assumptions
          `,
        },
        {
          id: "module-4-section-4",
          title: "BUILDING POSITIVE RELATIONSHIPS WITH COLLEAGUES, VOLUNTEERS, AND BENEFICIARIES",
          heading: "4.4 Building Positive Relationships with Colleagues, Volunteers, and Beneficiaries",
          content: `
Positive relationships are essential for effective teamwork, successful program implementation, and meaningful community engagement.
Strong professional relationships are built through:
trust
respect
cooperation
communication
reliability
mutual understanding
Building positive relationships requires consistent effort and professional behaviour over time.
This includes:
communicating respectfully
listening actively
supporting others when appropriate
resolving disagreements constructively
appreciating different perspectives
maintaining professional boundaries
Strong relationships do not mean that disagreements never occur. Differences in opinion are natural. What matters is how those differences are managed. Respectful discussion and collaborative problem-solving help maintain healthy working relationships even when perspectives differ.
Positive relationships contribute to stronger teamwork, improved communication, and more effective outcomes for individuals, organizations, and communities.

CORE UNDERSTANDING OF MODULE 4
Respect and diversity are fundamental to professional conduct. Individuals who treat others with dignity, appreciate diversity, prevent discrimination and harassment, and build positive relationships contribute to environments where people feel valued, included, and respected. These practices strengthen collaboration, trust, and effectiveness in both professional and community settings.

KEY TAKEAWAY
Every individual deserves to be treated with dignity and respect. By valuing diversity, promoting inclusion, preventing discrimination, and building positive relationships, professionals create environments where people can participate fully, contribute meaningfully, and work together effectively toward shared goals.
          `,
        },
      ],
    },
    {
      id: "module-5",
      title: "CONFIDENTIALITY AND DATA PROTECTION",
      sections: [
        {
          id: "module-5-section-1",
          title: "IMPORTANCE OF PROTECTING SENSITIVE INFORMATION",
          heading: "5.1 Importance of Protecting Sensitive Information",
          content: `
Sensitive information includes any data or details that, if shared inappropriately, could harm an individual, compromise their privacy, or affect their dignity.
This may include:
personal experiences shared during engagement sessions
health-related information
family or social circumstances
records of beneficiaries or participants
internal organizational information
Protecting such information is important because:
it builds trust between individuals and organizations
it encourages openness during engagement activities
it prevents harm or embarrassment to individuals
it supports ethical and professional practice
it ensures compliance with organizational standards
When individuals know that their information will be protected, they are more likely to communicate honestly and participate meaningfully in programs.
          `,
        },
        {
          id: "module-5-section-2",
          title: "RESPECTING PRIVACY OF BENEFICIARIES, VOLUNTEERS, AND STAKEHOLDERS",
          heading: "5.2 Respecting Privacy of Beneficiaries, Volunteers, and Stakeholders",
          content: `
Privacy refers to the right of individuals to control how their personal information is collected, used, and shared.
Respecting privacy involves:
collecting only necessary information
avoiding unnecessary questions or probing
ensuring information is shared only with authorized persons
protecting written and digital records
avoiding public discussion of private matters
In practice, individuals must be careful not to disclose sensitive information during informal conversations, meetings, or public discussions.
For example, if a participant shares personal family challenges during a group session, that information should not be repeated outside the professional setting or shared with others who are not involved in the process.
          `,
        },
        {
          id: "module-5-section-3",
          title: "RESPONSIBLE HANDLING OF INFORMATION AND RECORDS",
          heading: "5.3 Responsible Handling of Information and Records",
          content: `
Information and records in professional settings must be handled with care, accuracy, and responsibility.
This includes:
accurate documentation of activities and findings
secure storage of physical and digital records
proper transmission of reports and data
avoiding alteration or misrepresentation of information
ensuring information is accessible only to authorized individuals
Errors in handling information can lead to misinformed decisions, loss of trust, or harm to individuals involved.
For example, inaccurate reporting of community needs or beneficiary data can result in resources being misallocated or reaching the wrong individuals.
          `,
        },
        {
          id: "module-5-section-4",
          title: "APPROPRIATE USE OF SOCIAL MEDIA AND DIGITAL PLATFORMS",
          heading: "5.4 Appropriate Use of Social Media and Digital Platforms",
          content: `
Digital platforms are widely used for communication, documentation, and information sharing. However, they also carry risks if used irresponsibly.
Appropriate use of social media and digital platforms involves:
avoiding sharing confidential or sensitive information
obtaining consent before posting images or stories
maintaining professionalism in all online communication
ensuring that posts do not misrepresent individuals or organizations
separating personal opinions from professional representation
In professional settings, online content can easily reach wide audiences and may be interpreted as representing the organization, even if posted personally.
For example, sharing images from field activities without consent or posting details about beneficiaries can violate privacy and damage trust.

CORE UNDERSTANDING OF MODULE 5
Confidentiality and data protection are essential for maintaining trust, ensuring ethical practice, and protecting individuals’ rights. Professionals are expected to handle information responsibly by respecting privacy, managing records accurately, and using digital platforms appropriately. These practices safeguard both individuals and organizational credibility.

KEY TAKEAWAY
Sensitive information must always be treated with care. When confidentiality and data protection are respected, individuals feel safe, trust is strengthened, and professional relationships are maintained. Responsible handling of information is a key requirement in all professional and community-based work.
          `,
        },
      ],
    },
    {
      id: "module-6",
      title: "PROFESSIONAL COMMUNICATION",
      sections: [
        {
          id: "module-6-section-1",
          title: "EFFECTIVE VERBAL AND WRITTEN COMMUNICATION",
          heading: "6.1 Effective Verbal and Written Communication",
          content: `
Verbal communication refers to spoken interaction, while written communication refers to messages, reports, documentation, and other forms of written expression.
Effective verbal communication involves:
speaking clearly and respectfully
using appropriate tone and language
ensuring messages are easy to understand
adjusting communication based on the audience
avoiding offensive or unclear expressions
In community settings, verbal communication must often be simplified to ensure understanding across different education levels and backgrounds.
Written communication involves:
clarity and structure in reporting
accurate presentation of information
correct spelling and grammar where applicable
professional tone in messages and reports
avoiding ambiguity or misinterpretation
For example, field reports must clearly describe what was done, what was observed, and any challenges encountered, without exaggeration or omission.
          `,
        },
        {
          id: "module-6-section-2",
          title: "ACTIVE LISTENING",
          heading: "6.2 Active Listening",
          content: `
Active listening is the ability to fully focus on, understand, and respond appropriately to what others are saying.
It goes beyond hearing words and involves:
paying full attention to the speaker
avoiding interruptions
understanding the message before responding
observing non-verbal cues such as tone and body language
asking clarifying questions when necessary
Active listening is essential in community engagement because individuals may express concerns, experiences, or feedback that require careful understanding.
For example, during a community discussion, a participant may not directly express their concern clearly but may communicate it indirectly through examples or emotions. Active listening helps identify the real issue being shared.
          `,
        },
        {
          id: "module-6-section-3",
          title: "RESPECTFUL AND CONSTRUCTIVE FEEDBACK",
          heading: "6.3 Respectful and Constructive Feedback",
          content: `
Feedback refers to responses given about performance, behaviour, or outcomes with the intention of improvement.
Respectful and constructive feedback is:
specific rather than general
focused on behaviour rather than personal attributes
delivered in a respectful tone
aimed at improvement rather than criticism
supported by clear examples where possible
Constructive feedback is important in both team and field environments because it supports learning and continuous improvement.
For example, instead of saying “you did this wrongly,” constructive feedback focuses on the action and improvement needed, such as explaining what should be adjusted in future tasks.
          `,
        },
        {
          id: "module-6-section-4",
          title: "MAINTAINING PROFESSIONALISM IN ONLINE AND OFFLINE INTERACTIONS",
          heading: "6.4 Maintaining Professionalism in Online and Offline Interactions",
          content: `
Professional communication extends beyond face-to-face interaction to include digital communication platforms such as messaging apps, emails, and social media.
Maintaining professionalism in all interactions involves:
using respectful language in messages and discussions
avoiding emotional or offensive responses
ensuring accuracy before sharing information
maintaining confidentiality in digital spaces
separating personal communication from professional communication where necessary
Online communication can be easily shared or misinterpreted, making professionalism even more important.
For example, informal comments made in group chats or social media can be viewed as representing professional opinions, even when not intended that way.
Consistency in behaviour across both online and offline environments strengthens trust and professional credibility.

CORE UNDERSTANDING OF MODULE 6
Professional communication ensures that information is shared clearly, respectfully, and accurately across all forms of interaction. It includes verbal, written, listening, feedback, and digital communication practices. Effective communication strengthens understanding, reduces conflict, and supports collaboration in professional and community settings.

KEY TAKEAWAY
Communication is not only about speaking or writing—it is about ensuring understanding. Professionals who communicate clearly, listen actively, provide constructive feedback, and maintain professionalism in all interactions contribute to stronger relationships, better teamwork, and more effective outcomes.
          `,
        },
      ],
    },
  ],

  assessment: {
    mcq: [
      {
        id: 1,
        title: "Question 1",
        question: "What is professional conduct?",
        options: [
          "The expected standards of behaviour and responsibility in a professional environment",
          "Only the rules written in a policy document",
          "A type of financial report",
          "An informal community activity",
        ],
        correctAnswer: "The expected standards of behaviour and responsibility in a professional environment",
      },
      {
        id: 2,
        title: "Question 2",
        question: "Which of the following best describes ethics?",
        options: [
          "Moral principles that guide decisions and behaviour",
          "A set of office procedures",
          "A financial management system",
          "A communication skill only",
        ],
        correctAnswer: "Moral principles that guide decisions and behaviour",
      },
      {
        id: 3,
        title: "Question 3",
        question: "Why is professionalism important in volunteer work?",
        options: [
          "It helps build trust and improve community confidence",
          "It replaces all organizational policies",
          "It increases personal income",
          "It removes the need for teamwork",
        ],
        correctAnswer: "It helps build trust and improve community confidence",
      },
      {
        id: 4,
        title: "Question 4",
        question: "Integrity means:",
        options: [
          "Acting consistently with moral and professional standards",
          "Ignoring procedures when convenient",
          "Favouring friends in decision-making",
          "Avoiding responsibility",
        ],
        correctAnswer: "Acting consistently with moral and professional standards",
      },
      {
        id: 5,
        title: "Question 5",
        question: "Accountability requires a person to:",
        options: [
          "Accept ownership of actions and outcomes",
          "Blame others for mistakes",
          "Avoid reporting challenges",
          "Work without supervision only",
        ],
        correctAnswer: "Accept ownership of actions and outcomes",
      },
      {
        id: 6,
        title: "Question 6",
        question: "Respect in professional environments means:",
        options: [
          "Recognizing the dignity and rights of every person",
          "Supporting only people of the same background",
          "Ignoring the opinions of others",
          "Allowing discrimination if it is common",
        ],
        correctAnswer: "Recognizing the dignity and rights of every person",
      },
      {
        id: 7,
        title: "Question 7",
        question: "Which of the following is an example of a conflict of interest?",
        options: [
          "Using personal relationships to influence a decision",
          "Following an approved procedure",
          "Reporting accurate information",
          "Protecting confidential data",
        ],
        correctAnswer: "Using personal relationships to influence a decision",
      },
      {
        id: 8,
        title: "Question 8",
        question: "Why is confidentiality important?",
        options: [
          "It protects privacy and builds trust",
          "It allows personal information to be shared freely",
          "It replaces all documentation requirements",
          "It reduces the need for accountability",
        ],
        correctAnswer: "It protects privacy and builds trust",
      },
      {
        id: 9,
        title: "Question 9",
        question: "Which practice best supports inclusion?",
        options: [
          "Creating opportunities for everyone to participate meaningfully",
          "Limiting discussion to only a few people",
          "Assuming everyone has the same needs",
          "Ignoring different perspectives",
        ],
        correctAnswer: "Creating opportunities for everyone to participate meaningfully",
      },
      {
        id: 10,
        title: "Question 10",
        question: "Professional communication is most effective when it is:",
        options: [
          "Clear, respectful, and accurate",
          "Lengthy and emotional",
          "Unstructured and informal",
          "Only written and never spoken",
        ],
        correctAnswer: "Clear, respectful, and accurate",
      },
    ],

    theory: [
      {
        id: 1,
        title: "Case Study Question",
        bootomTitle: "END OF ASSESSMENT",
        question: `
Addie’s Afrique Foundation is preparing volunteers for community engagement work. During a planned outreach activity, volunteers are expected to communicate with parents, support facilitators, handle sensitive information, and maintain professional standards while working with people from different backgrounds.

Using your understanding of professional conduct and ethics, explain how a volunteer should:
1. Maintain professionalism and ethical behaviour during the outreach activity.
2. Respect diversity and communicate appropriately with different community members.
3. Protect confidential information and contribute positively to the team and the program.
        `,
      },
    ],
  },
};

export default course103;
